import React, { memo, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';

import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import cls from './TextEditor.module.css';

interface TextEditorProps {
    label?: string;
    inputName: string;
    value: string;
    onChange: (value: string) => void;
    classes?: string;
    textEditorContentClassName?: string;
    textEditorClassName?: string;
    placeholder?: string;
    readOnly?: boolean;
    isDisabled?: boolean;
    maxLength?: number;
    errorText?: string;
}

const escapeHtml = (text: string) =>
    text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const truncateToHtmlParagraphs = (text: string, maxLength: number) => {
    const truncated = text.slice(0, maxLength);

    const paragraphs = truncated
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);

    if (!paragraphs.length) {
        return '<p></p>';
    }

    return paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join('');
};

export const TextEditor = memo(
    forwardRef<HTMLDivElement | null, TextEditorProps>((props, ref) => {
        const { t } = useTranslation();

        const {
            label,
            inputName,
            value,
            onChange,
            classes,
            placeholder = t('TextEditor.props.placeholder.default_value'),
            isDisabled = false,
            maxLength = 2000,
            textEditorContentClassName,
            textEditorClassName,
            errorText,
        } = props;

        const editorContainerRef = useRef<HTMLDivElement | null>(null);
        const isTruncatingRef = useRef(false);

        const editor = useEditor({
            extensions: [
                StarterKit,
                Link.configure({
                    autolink: true,
                    openOnClick: true,
                    linkOnPaste: true,
                }),
                Placeholder.configure({
                    placeholder,
                    showOnlyWhenEditable: true,
                    emptyEditorClass: cls.isEmpty,
                }),
            ],
            content: value || '',
            editable: !isDisabled,
            onUpdate: ({ editor }) => {
                if (isTruncatingRef.current) {
                    return;
                }

                const plainText = editor.getText({ blockSeparator: '\n' });

                if (plainText.length > maxLength) {
                    const nextHtml = truncateToHtmlParagraphs(plainText, maxLength);

                    isTruncatingRef.current = true;
                    editor.commands.setContent(nextHtml, false);
                    onChange(nextHtml);
                    isTruncatingRef.current = false;

                    return;
                }

                onChange(editor.getHTML());
            },
        });

        useEffect(() => {
            if (!editor) return;

            const current = editor.getHTML();
            if ((value ?? '') !== (current ?? '')) {
                editor.commands.setContent(value || '', false);
            }
        }, [value, editor]);

        useImperativeHandle(ref, () => editorContainerRef.current as HTMLDivElement, []);

        if (!editor) return null;

        return (
            <div className={classNames(cls.TextEditorWrapper, classes)}>
                {label && (
                    <label htmlFor={inputName} className={cls.Label}>
                        {label}
                    </label>
                )}
                <div
                    ref={editorContainerRef}
                    className={classNames(
                        cls.TextEditor,
                        {
                            [cls.disabled]: isDisabled,
                        },
                        textEditorClassName
                    )}
                >
                    <EditorContent
                        editor={editor}
                        className={classNames(
                            cls.EditorContent,
                            textEditorContentClassName,
                            errorText && cls.EditorContentError
                        )}
                    />
                </div>
                {errorText && <span className={cls.ErrorText}>{errorText}</span>}
            </div>
        );
    })
);

TextEditor.displayName = 'TextEditor';
